import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAX_SECONDS = 120;

/** Tap to speak, tap to stop. Sends the recording to whisper-1 and returns the text. */
export default function VoiceInput({ onTranscript, purpose = "assistant", dark = false,
  label = "Speak" }) {
  const [state, setState] = useState("idle"); // idle | recording | working
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);

  useEffect(() => () => {
    clearInterval(timer.current);
    recorder.current?.stream?.getTracks().forEach((t) => t.stop());
  }, []);

  const send = async (blob) => {
    setState("working");
    try {
      const form = new FormData();
      form.append("file", blob, "voice-note.webm");
      const { data } = await api.post(`/ai/transcribe?purpose=${purpose}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onTranscript?.(data.transcript, data);
      toast.success("Heard you");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setState("idle");
      setSeconds(0);
    }
  };

  const stop = () => {
    clearInterval(timer.current);
    recorder.current?.stop();
    recorder.current?.stream?.getTracks().forEach((t) => t.stop());
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      return toast.error("This device or browser cannot record audio");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
        if (blob.size < 1200) {
          setState("idle");
          setSeconds(0);
          return toast.error("That was too short — hold on and speak clearly");
        }
        send(blob);
      };
      rec.start();
      recorder.current = rec;
      setState("recording");
      setSeconds(0);
      timer.current = setInterval(
        () => setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) stop();
          return s + 1;
        }),
        1000
      );
    } catch {
      toast.error("Microphone permission is needed to speak");
    }
  };

  if (state === "working") {
    return (
      <Button type="button" size="sm" variant={dark ? "secondary" : "outline"} disabled
        className="gap-1.5" data-testid="voice-input-working">
        <Loader2 className="h-4 w-4 animate-spin" /> Writing…
      </Button>
    );
  }

  if (state === "recording") {
    return (
      <Button type="button" size="sm" variant="destructive" onClick={stop}
        className="gap-1.5" data-testid="voice-input-stop">
        <Square className="h-3.5 w-3.5" /> Stop {seconds}s
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" variant={dark ? "secondary" : "outline"} onClick={start}
      className="gap-1.5" data-testid="voice-input-start">
      <Mic className="h-4 w-4" /> {label}
    </Button>
  );
}
